export interface AlertFilterState {
    searchTerm: string;
    selectedLocationId: string;
    selectedAssigneeId: string;
    selectedUnreadType: string;
    showOpportunities: boolean;
    showLeases: boolean;
    showUnassignedOpps: boolean;
}

export interface AlertFilterResult {
    filteredAlerts: any[];
    opportunityCount: number;
    leaseCount: number;
    unassignedOppCount: number;
    emailCount: number;
    smsCount: number;
    missedCallCount: number;
    assigneeCounts: Record<string, number>;
    allAssigneeCount: number;
}

function matchesCommonFilters(
    alert: any,
    state: AlertFilterState,
    options: { excludeAssignee?: boolean; excludeUnreadType?: boolean } = {}
): boolean {
    if (
        state.selectedLocationId !== 'all' &&
        String(alert?.location?.reference || '') !== String(state.selectedLocationId)
    ) {
        return false;
    }

    if (
        !options.excludeAssignee &&
        state.selectedAssigneeId !== 'all' &&
        String(alert?.user?.id || '') !== String(state.selectedAssigneeId)
    ) {
        return false;
    }

    if (!options.excludeUnreadType && state.selectedUnreadType !== 'all') {
        if (state.selectedUnreadType === 'missed_calls') {
            if (!alert?.is_missed_call) {
                return false;
            }
        } else if (alert?.type !== state.selectedUnreadType) {
            return false;
        }
    }

    const search = state.searchTerm.trim().toLowerCase();
    if (search) {
        const message = String(alert?.message || '').toLowerCase();
        const assigneeName = `${alert?.user?.first_name || ''} ${alert?.user?.last_name || ''}`
            .trim()
            .toLowerCase();

        if (!message.includes(search) && !assigneeName.includes(search)) {
            return false;
        }
    }

    return true;
}

function matchesSelectedCategory(alert: any, state: AlertFilterState): boolean {
    const module = alert?.conversation?.custom?.module;

    return (
        (state.showOpportunities && module === 1) ||
        (state.showLeases && module === 2) ||
        (state.showUnassignedOpps && !alert?.conversation?.custom)
    );
}

export function applyAlertFilters(alerts: any[], state: AlertFilterState): AlertFilterResult {
    const source = Array.isArray(alerts) ? alerts : [];

    const categoryCountAlerts = source.filter((alert) => matchesCommonFilters(alert, state));
    const assigneeCountAlerts = source.filter(
        (alert) =>
            matchesCommonFilters(alert, state, { excludeAssignee: true }) &&
            matchesSelectedCategory(alert, state)
    );
    const unreadTypeCountAlerts = source.filter(
        (alert) =>
            matchesCommonFilters(alert, state, { excludeUnreadType: true }) &&
            matchesSelectedCategory(alert, state)
    );

    const assigneeCounts = assigneeCountAlerts.reduce((counts, alert) => {
        const assigneeId = alert?.user?.id;
        if (assigneeId !== undefined && assigneeId !== null) {
            const key = String(assigneeId);
            counts[key] = (counts[key] || 0) + 1;
        }
        return counts;
    }, {} as Record<string, number>);

    return {
        filteredAlerts: categoryCountAlerts.filter((alert) => matchesSelectedCategory(alert, state)),
        opportunityCount: categoryCountAlerts.filter(
            (alert) => alert?.conversation?.custom?.module === 1
        ).length,
        leaseCount: categoryCountAlerts.filter(
            (alert) => alert?.conversation?.custom?.module === 2
        ).length,
        unassignedOppCount: categoryCountAlerts.filter(
            (alert) => !alert?.conversation?.custom
        ).length,
        emailCount: unreadTypeCountAlerts.filter((alert) => alert?.type === 'email').length,
        smsCount: unreadTypeCountAlerts.filter((alert) => alert?.type === 'message').length,
        missedCallCount: unreadTypeCountAlerts.filter((alert) => alert?.is_missed_call).length,
        assigneeCounts,
        allAssigneeCount: assigneeCountAlerts.length,
    };
}
